import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
  alpha,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Link
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  DeviceHub as DeviceHubIcon,
  Router as RouterIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ExitToApp as ExitToAppIcon,
  AssignmentReturn as AssignmentReturnIcon,
  Close as CloseIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import {
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';

import { Equipment, EquipmentFilters, Brand, Model } from '../types';

// Definir o tipo EquipmentStats localmente para evitar problemas de cache
interface EquipmentStats {
  summary: {
    total: number;
    free: number;
    inUse: number;
    totalBrands: number;
  };
  byType: Array<{
    tipo: string;
    total: number;
  }>;
  byModality: Array<{
    modalidade: string;
    total: number;
  }>;
  byStatus: Array<{
    status_uso: string;
    total: number;
  }>;
}
import { Fornecedor } from '../types/fornecedor';
import equipmentService from '../services/equipmentService';
import { FornecedorService } from '../services/fornecedorService';
import BrandForm from '../components/forms/BrandForm';
import ModelForm from '../components/forms/ModelForm';
import EquipmentForm from '../components/forms/EquipmentForm';
import ModelsManagerModal from '../components/modals/ModelsManagerModal';
import LancarSaidaModal from '../components/modals/LancarSaidaModal';
import LancarDevolucaoModal from '../components/modals/LancarDevolucaoModal';
import FornecedorSelect from '../components/selects/FornecedorSelect';
import EscritorioSelect from '../components/selects/EscritorioSelect';
import FuncionariosSelect from '../components/selects/FuncionariosSelect';
import SysDateRangePicker from '../components/SysDateRangePicker';
import { useRelatorioCores } from './relatorios';

// Funções auxiliares
const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const EquipmentsPage: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados principais
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [stats, setStats] = useState<EquipmentStats | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estados para filtros (search da URL aplicado na abertura, ex.: link da ONU por gpon_sn)
  const [filters, setFilters] = useState<EquipmentFilters>(() => ({
    tipo: '',
    marcaId: undefined,
    modelo_id: undefined,
    modalidade: '',
    status_uso: undefined,
    fornecedor_id: undefined,
    escritorio_id: undefined,
    funcionario_responsavel: undefined,
    data_inicio: '',
    data_fim: '',
    search: searchParams.get('search') ?? ''
  }));

  // Estados para modais
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [brandManagerOpen, setBrandManagerOpen] = useState(false);
  const [modelManagerOpen, setModelManagerOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Estado para modais de saída e devolução de equipamentos
  const [saidaModalOpen, setSaidaModalOpen] = useState(false);
  const [devolucaoModalOpen, setDevolucaoModalOpen] = useState(false);

  // Estados para visualização de imagem
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  // Estados para formulários dentro dos gerenciadores
  const [brandFormOpen, setBrandFormOpen] = useState(false);
  const [modelFormOpen, setModelFormOpen] = useState(false);

  // Estados para configurações
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [gerenciarPorTecnico, setGerenciarPorTecnico] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Carregar dados
  const loadEquipments = useCallback(async () => {
    try {
      setLoading(true);

      const apiFilters: any = {
        tipo: filters.tipo || undefined,
        marcaId: filters.marcaId || undefined,
        modelo_id: filters.modelo_id || undefined,
        modalidade: filters.modalidade || undefined,
        status_uso: filters.status_uso || undefined,
        fornecedor_id: filters.fornecedor_id || undefined,
        escritorio_id: filters.escritorio_id || undefined,
        funcionario_responsavel: filters.funcionario_responsavel || undefined,
        data_inicio: filters.data_inicio || undefined,
        data_fim: filters.data_fim || undefined,
        search: filters.search || undefined,
        page: page + 1,
        limit: rowsPerPage
      };

      const response = await equipmentService.getEquipments(apiFilters);
      if (response.success && response.data) {
        // Verificar se response.data é um array ou objeto com propriedades
        if (Array.isArray(response.data)) {
          // Se for array direto
          setEquipments(response.data);
          setTotalCount(response.data.length);
        } else {
          // Se for objeto com propriedades
          const equipmentsData = (response.data as any).equipments || response.data;
          setEquipments(Array.isArray(equipmentsData) ? equipmentsData : []);

          // A paginação vem em response.data.pagination
          const pagination = (response.data as any).pagination;
          setTotalCount(pagination?.total || 0);

          // As estatísticas podem vir em response.data.stats
          const stats = (response.data as any).stats;
          if (stats) {
            setStats(stats);
          }
        }
      } else {
        // Se não há dados válidos, definir array vazio
        setEquipments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
      showAlert('error', 'Erro ao carregar equipamentos');
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  const loadBrands = useCallback(async () => {
    try {
      const response = await equipmentService.getBrands();
      if (response.success && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const response = await equipmentService.getModels();
      if (response.success && response.data) {
        setModels(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  }, []);

  const loadFornecedores = useCallback(async () => {
    try {
      const response = await FornecedorService.buscarFornecedoresComFiltros({ limit: 100 });
      if (response.success && response.data) {
        setFornecedores(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await equipmentService.getStats(filters);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [filters]);

  // Carregar configurações
  const loadConfigs = useCallback(async () => {
    try {
      const response = await equipmentService.getConfiguration('gerenciar_estoque_por_tecnico');
      if (response.success && response.data) {
        setGerenciarPorTecnico(response.data.valor === 'true');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  }, []);

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  // Sincronizar filtro de busca com a URL (ex.: ao abrir link da ONU por gpon_sn)
  useEffect(() => {
    const q = searchParams.get('search') ?? '';
    setFilters(prev => (prev.search === q ? prev : { ...prev, search: q }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    loadBrands();
    loadModels();
    loadFornecedores();
    loadStats();
    loadConfigs();
  }, [loadBrands, loadModels, loadFornecedores, loadStats, loadConfigs]);

  // Recarregar estatísticas quando os filtros mudarem
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleFilterChange = (field: keyof EquipmentFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset page when filter changes
  };

  const resetFilters = () => {
    setFilters({
      tipo: '',
      marcaId: undefined,
      modelo_id: undefined,
      modalidade: '',
      status_uso: undefined,
      fornecedor_id: undefined,
      escritorio_id: undefined,
      funcionario_responsavel: undefined,
      data_inicio: '',
      data_fim: '',
      search: ''
    });
    setPage(0);
  };

  // Handlers para equipamentos
  const handleEditEquipment = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setEquipmentFormOpen(true);
  };

  const handleDeleteEquipment = async (equipment: Equipment) => {
    if (!window.confirm(`Deseja realmente excluir o equipamento ${equipment.numero_serie}?`)) {
      return;
    }

    try {
      await equipmentService.deleteEquipment(equipment.id);
      showAlert('success', 'Equipamento excluído com sucesso');
      loadEquipments();
    } catch (error: any) {
      console.error('Erro ao excluir equipamento:', error);
      showAlert('error', error?.response?.data?.message || 'Erro ao excluir equipamento');
    }
  };

  // Handlers para marcas
  const handleEditBrand = (brand: Brand) => {
    setSelectedBrand(brand);
    setBrandFormOpen(true);
  };

  const handleDeleteBrand = async (brand: Brand) => {
    if (!window.confirm(`Deseja realmente excluir a marca ${brand.titulo}?`)) {
      return;
    }

    try {
      await equipmentService.deleteBrand(brand.id);
      showAlert('success', 'Marca excluída com sucesso');
      loadBrands();
      loadModels(); // Recarregar modelos pois podem ter sido afetados
    } catch (error: any) {
      console.error('Erro ao excluir marca:', error);
      showAlert('error', error?.response?.data?.message || 'Erro ao excluir marca');
    }
  };

  // Handlers para modelos
  const handleEditModel = (model: Model) => {
    setSelectedModel(model);
    setModelFormOpen(true);
  };

  const handleDeleteModel = async (model: Model) => {
    if (!window.confirm(`Deseja realmente excluir o modelo ${model.titulo}?`)) {
      return;
    }

    try {
      await equipmentService.deleteModel(model.id);
      showAlert('success', 'Modelo excluído com sucesso');
      loadModels();
      loadEquipments(); // Recarregar equipamentos pois podem ter sido afetados
    } catch (error: any) {
      console.error('Erro ao excluir modelo:', error);
      showAlert('error', error?.response?.data?.message || 'Erro ao excluir modelo');
    }
  };

  const handleFormSuccess = () => {
    showAlert('success', 'Operação realizada com sucesso');
    loadEquipments();
    loadBrands();
    loadModels();
  };

  const handleModelFormSuccess = () => {
    showAlert('success', 'Modelo salvo com sucesso');
    loadModels();
    loadEquipments(); // Recarregar equipamentos pois podem ter sido afetados
  };

  const handleBrandFormClose = () => {
    setBrandFormOpen(false);
    setSelectedBrand(null);
  };

  const handleModelFormClose = () => {
    setModelFormOpen(false);
    setSelectedModel(null);
  };

  const handleSaveConfig = async (newValue: boolean) => {
    try {
      setSavingConfig(true);
      setGerenciarPorTecnico(newValue);

      const response = await equipmentService.updateConfiguration('gerenciar_estoque_por_tecnico', newValue ? 'true' : 'false');
      if (!response.success) {
        throw new Error('Falha ao atualizar');
      }
      showAlert('success', 'Configuração atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      showAlert('error', 'Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleEquipmentFormClose = () => {
    setEquipmentFormOpen(false);
    setSelectedEquipment(null);
  };

  // Render dos widgets de estatísticas
  const renderStatsWidgets = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                📊 Por Tipo
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>
                  <TableBody>
                    <TableRow hover sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>
                      <TableCell>
                        Equipamentos em Uso
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold" variant="body2">
                          {stats.summary.inUse}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow hover sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>
                      <TableCell>
                        Equipamentos Livres
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold" variant="body2">
                          {stats.summary.free}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow hover sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>
                      <TableCell>
                        Equipamentos Total
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold" variant="body2">
                          {stats.summary.total}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                📊 Por Tipo
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>

                  <TableBody>
                    {stats.byType.map((item, index) => (
                      <TableRow key={item.tipo} hover sx={{ '& .MuiTableCell-root': { py: 0.25 } }}>
                        <TableCell>
                          <Chip
                            label={equipmentService.getTipoLabel(item.tipo)}
                            color={item.tipo === 'aberto' ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.625rem' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight="bold" variant="body2">
                            {item.total}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )) || (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 1 }}>
                            <Typography color="text.secondary" variant="caption">
                              Nenhum dado disponível
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render dos filtros
  const renderFilters = () => (
    <Paper sx={{ p: 1, mb: 1 }}>
      <Typography variant="h6" gutterBottom>
        Filtros
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filters.tipo}
              onChange={(e) => handleFilterChange('tipo', e.target.value)}
              label="Tipo"
            >
              <MenuItem value="">Todos os tipos</MenuItem>
              <MenuItem value="onu">ONU</MenuItem>
              <MenuItem value="ont">ONT</MenuItem>
              <MenuItem value="roteador">Roteador</MenuItem>
              <MenuItem value="switch">Switch</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Marca</InputLabel>
            <Select
              value={filters.marcaId || ''}
              onChange={(e) => handleFilterChange('marcaId', e.target.value || undefined)}
              label="Marca"
            >
              <MenuItem value="">Todas as marcas</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.titulo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Modalidade</InputLabel>
            <Select
              value={filters.modalidade}
              onChange={(e) => handleFilterChange('modalidade', e.target.value)}
              label="Modalidade"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="proprio">Próprio</MenuItem>
              <MenuItem value="comodato">Comodato</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status de Uso</InputLabel>
            <Select
              value={filters.status_uso || ''}
              onChange={(e) => handleFilterChange('status_uso', e.target.value || undefined)}
              label="Status de Uso"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="livre">Livre</MenuItem>
              <MenuItem value="em_uso">Em Uso</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FornecedorSelect
            value={filters.fornecedor_id}
            onChange={(id) => handleFilterChange('fornecedor_id', id)}
            label="Fornecedor"
            placeholder="Todos os fornecedores"
            fullWidth
            size="small"
            allowClear={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FuncionariosSelect
            value={filters.funcionario_responsavel ?? null}
            onChange={(id) => handleFilterChange('funcionario_responsavel', id ?? undefined)}
            label="Responsável"
            placeholder="Todos"
            size="small"
            fullWidth
            allowClear={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Buscar"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="N° série, MAC, GPON..."
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <EscritorioSelect
            value={filters.escritorio_id}
            onChange={(id) => handleFilterChange('escritorio_id', id)}
            label="Escritório"
            placeholder="Todos os escritórios"
            fullWidth
            size="small"
            allowClear={true}
            onlyActive={true}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <SysDateRangePicker
            onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
              const toYmd = (d: Date | null) => d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '';
              handleFilterChange('data_inicio', toYmd(range.startDate));
              handleFilterChange('data_fim', toYmd(range.endDate));
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1}>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={resetFilters}
              startIcon={<RefreshIcon />}
            >
              Limpar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  // Render da tabela de equipamentos
  const primario = theme.palette.primary.main;
  const renderEquipmentsTable = () => (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-head': {
                bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                fontWeight: 700,
                fontSize: '0.73rem',
                py: 0.8,
                borderBottom: `2px solid ${alpha(primario, 0.2)}`,
              },
            }}>
              <TableCell>Tipo</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Equipamento</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>N° Série</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>MAC/GPON SN</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Modalidade</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Status</TableCell>
              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(equipments || []).map((equipment) => (
              <TableRow key={equipment.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                  <Chip
                    label={equipmentService.getTipoLabel(equipment.modelo?.tipo || '')}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  <Box display="flex" alignItems="center">
                    <Avatar
                      src={equipment.modelo?.imagem ? equipmentService.getModelImageUrl(equipment.modelo.imagem) : undefined}
                      sx={{ mr: 1.5, width: 32, height: 32, cursor: equipment.modelo?.imagem ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (equipment.modelo?.imagem) {
                          setSelectedImageSrc(equipmentService.getModelImageUrl(equipment.modelo.imagem));
                          setImageModalOpen(true);
                        }
                      }}
                    >
                      <RouterIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {equipment.modelo?.marca?.titulo} {equipment.modelo?.titulo}
                      </Typography>
                      {equipment.modelo && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {equipment.modelo.portas_lan || equipment.modelo.portas_lan} portas LAN
                          {equipment.modelo.frequencia_wifi && ` • WiFi ${equipment.modelo.frequencia_wifi}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{equipment.numero_serie}</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  <Box>
                    {equipment.mac_address && (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        MAC: {equipment.mac_address}
                      </Typography>
                    )}
                    {equipment.gpon_sn && (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        GPON: {equipment.gpon_sn}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  <Chip
                    label={equipmentService.getModalidadeLabel(equipment.modalidade)}
                    size="small"
                    color={equipmentService.getModalidadeColor(equipment.modalidade)}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {equipment.status_uso === 'em_uso' && equipment.contrato?.cliente ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexWrap: 'wrap'
                      }}
                    >
                      <PersonIcon sx={{ color: 'success.main' }} />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/clients/${equipment.contrato?.cliente?.id}`)}
                        sx={{
                          textDecoration: 'none',
                          color: 'success.main',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'success.dark'
                          }
                        }}
                      >
                        {equipment.contrato?.cliente?.nome}
                      </Link>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        -
                      </Typography>
                      <AssignmentIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/contracts/${equipment.contrato?.id}?tab=equipments`)}
                        sx={{
                          textDecoration: 'none',
                          color: 'primary.main',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark'
                          }
                        }}
                      >
                        {equipment.contrato?.titulo}
                      </Link>
                    </Box>
                  ) : (
                    <Chip
                      label={equipmentService.getStatusUsoLabel(equipment.status_uso)}
                      size="small"
                      color={equipmentService.getStatusUsoColor(equipment.status_uso)}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 1.5, whiteSpace: 'nowrap' }}>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => handleEditEquipment(equipment)}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteEquipment(equipment)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.5,
            px: 2,
            bgcolor: alpha(primario, 0.04),
            borderTop: `2px solid ${alpha(primario, 0.2)}`,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            labelRowsPerPage="Linhas por página:"
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: 34,
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2,
                '& .MuiTablePagination-spacer': { display: 'none' },
              },
              '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
              '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
              '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
              '.MuiTablePagination-actions': { ml: 0.5 },
            }}
          />
        </Box>
      )}
    </Paper>
  );

  // Render do gerenciador de marcas
  const renderBrandManager = () => (
    <Dialog
      open={brandManagerOpen}
      onClose={() => setBrandManagerOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Gerenciar Marcas
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setBrandFormOpen(true)}
          sx={{ float: 'right' }}
        >
          Nova Marca
        </Button>
      </DialogTitle>
      <DialogContent>
        <List>
          {brands.map((brand) => (
            <ListItem key={brand.id} divider>
              <ListItemText
                primary={brand.titulo}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleEditBrand(brand)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  color="error"
                  onClick={() => handleDeleteBrand(brand)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBrandManagerOpen(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );



  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(cores.secundario, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(cores.primario, 0.12), color: cores.primario, width: 40, height: 40 }}>
              <DeviceHubIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Equipamentos
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Controle todos os equipamentos do sistema
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setEquipmentFormOpen(true)}
              disabled={models.length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Cadastrar Equipamento
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={<ExitToAppIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSaidaModalOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Lançar Saída
            </Button>
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<AssignmentReturnIcon sx={{ fontSize: 16 }} />}
              onClick={() => setDevolucaoModalOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Lançar Devolução
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<BusinessIcon sx={{ fontSize: 16 }} />}
              onClick={() => setBrandManagerOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
            >
              Gerenciar Marcas
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CategoryIcon sx={{ fontSize: 16 }} />}
              onClick={() => setModelManagerOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
            >
              Gerenciar Modelos
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              onClick={() => setConfigModalOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
            >
              Configurações
            </Button>
          </Box>
        </Box>
      </Paper>
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 1.5, fontSize: '0.8rem' }}>
          {alert.message}
        </Alert>
      )}

      {/* Verificar se há modelos cadastrados */}
      {models.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Para cadastrar equipamentos, primeiro você precisa cadastrar marcas e modelos.
          Use os botões "Gerenciar Marcas" e "Gerenciar Modelos" acima.
        </Alert>
      )}
      {/* Widgets de estatísticas */}
      {renderStatsWidgets()}

      {/* Filtros */}
      {renderFilters()}

      {/* Tabela de equipamentos */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Carregando equipamentos...</Typography>
        </Box>
      ) : (
        renderEquipmentsTable()
      )}

      {/* Modais */}
      <EquipmentForm
        open={equipmentFormOpen}
        onClose={handleEquipmentFormClose}
        onSuccess={handleFormSuccess}
        equipment={selectedEquipment}
      />

      <BrandForm
        open={brandFormOpen}
        onClose={handleBrandFormClose}
        onSuccess={handleFormSuccess}
        brand={selectedBrand}
      />

      <ModelForm
        open={modelFormOpen}
        onClose={handleModelFormClose}
        onSuccess={handleModelFormSuccess}
        model={selectedModel}
      />

      {/* Modal de Saída de Equipamentos */}
      <LancarSaidaModal
        open={saidaModalOpen}
        onClose={() => setSaidaModalOpen(false)}
        onSuccess={() => {
          showAlert('success', 'Saída de equipamentos lançada com sucesso!');
          loadEquipments();
          loadStats();
        }}
      />

      {/* Modal de Devolução de Equipamentos */}
      <LancarDevolucaoModal
        open={devolucaoModalOpen}
        onClose={() => setDevolucaoModalOpen(false)}
        onSuccess={() => {
          showAlert('success', 'Devolução de equipamentos lançada com sucesso!');
          loadEquipments();
          loadStats();
        }}
      />

      {/* Gerenciadores */}
      {renderBrandManager()}

      <ModelsManagerModal
        open={modelManagerOpen}
        onClose={() => setModelManagerOpen(false)}
        onEditModel={handleEditModel}
        onDeleteModel={handleDeleteModel}
        onCreateModel={() => setModelFormOpen(true)}
        brands={brands}
      />

      {/* Modal de Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Imagem do Modelo
          <IconButton onClick={() => setImageModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
          {selectedImageSrc && (
            <img src={selectedImageSrc} alt="Modelo" style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px' }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Configurações */}
      <Dialog
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Configurações de Estoque
          <IconButton onClick={() => setConfigModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Ajuste as regras de visibilidade de equipamentos para os técnicos no App.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, bgcolor: alpha(cores.primario, 0.05) }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Gerenciar estoque por Técnico
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Filtra equipamentos vinculados ao técnico no App.
                </Typography>
              </Box>
              {savingConfig ? (
                <CircularProgress size={24} />
              ) : (
                <Switch
                  checked={gerenciarPorTecnico}
                  onChange={(e) => handleSaveConfig(e.target.checked)}
                  color="primary"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigModalOpen(false)} variant="contained" size="small">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setEquipmentFormOpen(true)}
          disabled={models.length === 0}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default EquipmentsPage; 